import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../../styles/ProfessionalBlue.css';

const ProfessionalBlue = ({ isPreview = true }) => {
  const { eventName } = useParams();
  const [isRevealed, setIsRevealed] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [rsvpData, setRsvpData] = useState({
    name: '',
    email: '',
    company: '',
    attending: '',
    guests: '1',
    dietary: ''
  });

  // Parse event name
  const formatName = (name) => {
    if (!name) return 'Corporate Event';
    return name.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const invitationData = {
    eventName: formatName(eventName),
    eventType: 'Annual Business Conference',
    tagline: 'Innovate • Collaborate • Succeed',
    eventDate: 'November 15, 2026',
    eventDay: 'Sunday',
    eventTime: '9:00 AM - 5:00 PM',
    venue: 'Manila Grand Ballroom',
    venueAddress: 'Makati Avenue, Makati City',
    organizer: 'ABC Corporation',
    message: 'We cordially invite you to join us for an inspiring day of innovation, networking, and professional growth.',
    agenda: [
      { time: '9:00 AM', activity: 'Registration & Networking' },
      { time: '10:00 AM', activity: 'Keynote Address' },
      { time: '12:00 NN', activity: 'Networking Lunch' },
      { time: '2:00 PM', activity: 'Panel Discussion' },
      { time: '4:00 PM', activity: 'Closing Remarks' }
    ]
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsRevealed(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="professional-blue">
      {/* Professional Elements */}
      <div className="professional-elements">
        <div className="grid-pattern"></div>
        <div className="accent-line line-1"></div>
        <div className="accent-line line-2"></div>
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
      </div>

      {/* Main Content */}
      <div className={`professional-container ${isRevealed ? 'revealed' : ''}`}>
        {/* Navigation */}
        <nav className="professional-nav">
          <button 
            className={activeSection === 'home' ? 'active' : ''} 
            onClick={() => setActiveSection('home')}
          >
            Overview
          </button>
          <button 
            className={activeSection === 'agenda' ? 'active' : ''} 
            onClick={() => setActiveSection('agenda')}
          >
            Agenda
          </button>
          <button 
            className={activeSection === 'rsvp' ? 'active' : ''} 
            onClick={() => setActiveSection('rsvp')}
          >
            Register
          </button>
        </nav>

        {/* Home Section */}
        {activeSection === 'home' && (
          <section className="professional-section home-section">
            <div className="professional-frame">
              <div className="brand-header">
                <div className="company-logo">
                  <span className="logo-icon">◆</span>
                  <span className="logo-text">{invitationData.organizer}</span>
                </div>
                <span className="event-badge">{invitationData.eventType}</span>
              </div>

              <div className="home-content">
                <h1 className="event-title">{invitationData.eventName}</h1>
                <p className="event-tagline">{invitationData.tagline}</p>

                <div className="professional-divider">
                  <span className="divider-line"></span>
                </div>

                <p className="invitation-message">{invitationData.message}</p>

                <div className="event-details-grid">
                  <div className="detail-item">
                    <span className="detail-icon">📅</span>
                    <div className="detail-text">
                      <span className="detail-label">Date</span>
                      <span className="detail-value">{invitationData.eventDate}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">⏰</span>
                    <div className="detail-text">
                      <span className="detail-label">Time</span>
                      <span className="detail-value">{invitationData.eventTime}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">📍</span>
                    <div className="detail-text">
                      <span className="detail-label">Venue</span>
                      <span className="detail-value">{invitationData.venue}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">🏢</span>
                    <div className="detail-text">
                      <span className="detail-label">Address</span>
                      <span className="detail-value">{invitationData.venueAddress}</span>
                    </div>
                  </div>
                </div>

                <button 
                  className="professional-btn primary"
                  onClick={() => setActiveSection('rsvp')}
                >
                  Register Now
                </button>
                <button 
                  className="professional-btn secondary"
                  onClick={() => setActiveSection('agenda')}
                >
                  View Agenda
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Agenda Section */}
        {activeSection === 'agenda' && (
          <section className="professional-section agenda-section">
            <h2 className="section-title">Event Agenda</h2>
            <p className="section-subtitle">{invitationData.eventDate}</p>

            <div className="agenda-timeline">
              {invitationData.agenda.map((item, index) => (
                <div key={index} className="agenda-item">
                  <div className="agenda-time">
                    <span className="time-badge">{item.time}</span>
                  </div>
                  <div className="agenda-connector">
                    <span className="connector-dot"></span>
                    {index < invitationData.agenda.length - 1 && <span className="connector-line"></span>}
                  </div>
                  <div className="agenda-content">
                    <span className="activity-name">{item.activity}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="info-cards">
              <div className="info-card">
                <span className="card-icon">👔</span>
                <h4>Dress Code</h4>
                <p>Business Formal</p>
              </div>
              <div className="info-card">
                <span className="card-icon">🅿️</span>
                <h4>Parking</h4>
                <p>Free Parking Available</p>
              </div>
              <div className="info-card">
                <span className="card-icon">📶</span>
                <h4>WiFi</h4>
                <p>Complimentary Access</p>
              </div>
            </div>

            <button 
              className="professional-btn primary"
              onClick={() => setActiveSection('rsvp')}
            >
              Register Now
            </button>
          </section>
        )}

        {/* RSVP Section */}
        {activeSection === 'rsvp' && (
          <section className="professional-section rsvp-section">
            <h2 className="section-title">Event Registration</h2>
            <p className="section-subtitle">Secure your spot today</p>

            <form className="professional-rsvp-form" onSubmit={(e) => e.preventDefault()}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={rsvpData.name}
                    placeholder="Enter your full name"
                    disabled={isPreview}
                  />
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={rsvpData.email}
                    placeholder="Enter your email"
                    disabled={isPreview}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Company / Organization</label>
                  <input
                    type="text"
                    name="company"
                    value={rsvpData.company}
                    placeholder="Enter company name"
                    disabled={isPreview}
                  />
                </div>
                <div className="form-group">
                  <label>Number of Attendees</label>
                  <select
                    name="guests"
                    value={rsvpData.guests}
                    disabled={isPreview}
                  >
                    <option value="1">1 Person</option>
                    <option value="2">2 People</option>
                    <option value="3">3 People</option>
                    <option value="4">4 People</option>
                    <option value="5">5+ People</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Will you be attending?</label>
                <div className="attendance-options">
                  <label className={`attendance-option ${isPreview ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="attending"
                      value="yes"
                      disabled={isPreview}
                    />
                    <span className="option-check">✓</span>
                    <span className="option-text">Yes, I'll attend</span>
                  </label>
                  <label className={`attendance-option ${isPreview ? 'disabled' : ''}`}>
                    <input
                      type="radio"
                      name="attending"
                      value="no"
                      disabled={isPreview}
                    />
                    <span className="option-check">✗</span>
                    <span className="option-text">Unable to attend</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Dietary Requirements (Optional)</label>
                <input
                  type="text"
                  name="dietary"
                  value={rsvpData.dietary}
                  placeholder="Any dietary restrictions or allergies"
                  disabled={isPreview}
                />
              </div>

              <button type="submit" className="professional-btn primary full-width" disabled={isPreview}>
                Complete Registration
              </button>

              <p className="form-note">
                * Registration deadline: November 1, 2026
              </p>
            </form>
          </section>
        )}

        {/* Watermark */}
        <div className="professional-watermark">
          <a href="/invitations" className="back-to-templates-btn">Back to Designs</a>
          <p>Powered by <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer">Logica Technology</a></p>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalBlue;
